import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
} from "../../../../../global/forms/GlobalFormFields";
import { SCHEDULE_MASTER_STATUS_OPTIONS } from "../../../utils/scheduleMasterStatusOptions";
import {
  scheduleStatusSchema,
  scheduleStatusFormDefaults,
} from "../../../utils/scheduleMasterValidation";
import { mapApiDetailsToFormErrors } from "../../../utils/scheduleHelpers";

const ALERT_ID = "schedule-status-form-alert";

export default function ScheduleStatusForm({
  mode = "create",
  initialValues,
  onSubmit,
  onCancel,
  submitting = false,
  serverError = "",
}) {
  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(scheduleStatusSchema),
    defaultValues: initialValues || scheduleStatusFormDefaults,
    mode: "onBlur",
  });

  useEffect(() => {
    if (initialValues) reset(initialValues);
  }, [initialValues, reset]);

  const submitHandler = async (values) => {
    try {
      await onSubmit?.({
        status_name: String(values.status_name ?? "").trim(),
        status_id: Number(values.status_id),
      });
    } catch (err) {
      if (err?.details) {
        const mapped = mapApiDetailsToFormErrors(err.details);
        Object.entries(mapped).forEach(([field, message]) => {
          setError(field, { type: "server", message });
        });
      }
      throw err;
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} noValidate>
      <GlobalFormShell
        legend={mode === "edit" ? "Edit schedule status" : "Add schedule status"}
        alertId={ALERT_ID}
        showAlert={Boolean(serverError)}
        alertText={serverError}
        gridClassName="gfs-form-grid--compact"
        actions={
          <>
            {onCancel ? (
              <GlobalButton type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </GlobalButton>
            ) : null}
            <GlobalButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Saving…" : mode === "edit" ? "Save changes" : "Create status"}
            </GlobalButton>
          </>
        }
      >
        <Controller
          name="status_name"
          control={control}
          render={({ field }) => (
            <GlobalTextField
              id="status_name"
              label="Status name"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.status_name?.message}
              placeholder="e.g. Pending, Upcoming"
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="status_id"
          control={control}
          render={({ field }) => (
            <GlobalSelectField
              id="schedule_status_master_status_id"
              label="Active flag"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.status_id?.message}
              options={SCHEDULE_MASTER_STATUS_OPTIONS}
              placeholderOption="Select status"
              baseAlertId={ALERT_ID}
            />
          )}
        />
      </GlobalFormShell>
    </form>
  );
}
